import React from 'react';

import { useClassnames } from '@lumapps/classnames';
import { get as getConfig } from '@lumapps/constants';
import { AppId } from '@lumapps/constants/app';
import { useSlideshowControlLabels } from '@lumapps/lumx/hooks/useSlideshowControlLabels';
import { Lightbox, Slideshow, SlideshowItem, ImageBlock } from '@lumapps/lumx/react';
import { ClickAwayProvider } from '@lumapps/lumx/react/utils';
import { sendWebviewMobileMessage } from '@lumapps/mobile-webview/message-emit';
import { addLocationOriginToUrl } from '@lumapps/router/utils';
import { GLOBAL, useTranslate } from '@lumapps/translations';
import { generateRandomWithSeed } from '@lumapps/utils/string/generateRandomWithSeed';

import { ImageLightboxProps } from '../../types';

import './index.scss';

const { applicationId } = getConfig();

const CLASSNAME = 'image-lightbox';

/**
 * An image slideshow lightbox
 *
 * Provides:
 * - Correct image positioning
 * - Good focus on open
 * - Fallback on webview app
 * - Provide click away
 *
 * @family Images
 * @param ImageLightboxProps
 * @returns ImageLightbox
 */
export const ImageLightbox: React.FC<ImageLightboxProps> = ({
    className,
    isOpen,
    onClose,
    parentElement,
    slideshowActiveIndex,
    thumbnails,
    app = applicationId,
}) => {
    const { translateKey } = useTranslate();
    const slideshowLabels = useSlideshowControlLabels();
    const currentPaginationItemRef = React.useRef(null);
    const lightboxRef = React.useRef<HTMLDivElement>(null);
    const { block, element } = useClassnames(CLASSNAME);

    // Click away config
    const clickAwayChildrenRefs = React.useMemo(() => {
        return {
            get current() {
                // Consider all button and img as inside, everything else is a outside
                const children = lightboxRef.current?.querySelectorAll('button, img');
                return children ? Array.from(children).map((current) => ({ current: current as HTMLElement })) : [];
            },
        };
    }, []);

    const isWebview = app === AppId.webview;
    React.useEffect(() => {
        if (!isWebview || !isOpen) {
            return;
        }

        /*
         * In webview app: send images to the host mobile app instead.
         */
        const images = thumbnails.map(({ image }) => ({ image: addLocationOriginToUrl(image) }));
        const activeImageIndex = slideshowActiveIndex || 0;
        sendWebviewMobileMessage({
            type: 'open-image-slideshow',
            payload: { images, activeImageIndex },
        });
    }, [isOpen, isWebview, slideshowActiveIndex, thumbnails]);

    if (isWebview) {
        return null;
    }

    return (
        <Lightbox
            ref={lightboxRef}
            aria-label={
                thumbnails.length > 1
                    ? translateKey(GLOBAL.FULLSCREEN_IMAGE_SLIDESHOW)
                    : translateKey(GLOBAL.FULLSCREEN_IMAGE)
            }
            className={block(undefined, className)}
            parentElement={parentElement}
            isOpen={isOpen}
            onClose={onClose}
            closeButtonProps={{ label: translateKey(GLOBAL.CLOSE) }}
            focusElement={currentPaginationItemRef}
        >
            <ClickAwayProvider childrenRefs={clickAwayChildrenRefs} callback={onClose}>
                <Slideshow
                    theme="dark"
                    activeIndex={slideshowActiveIndex}
                    slideshowControlsProps={{
                        nextButtonProps: { label: slideshowLabels.next },
                        previousButtonProps: { label: slideshowLabels.previous },
                        paginationItemProps: (index) => ({
                            label: slideshowLabels.paginationItem(index),
                            // Focus the active pagination item once on mount
                            ref: slideshowActiveIndex === index ? currentPaginationItemRef : undefined,
                        }),
                    }}
                    slideGroupLabel={slideshowLabels.slideGroup}
                    fillHeight
                >
                    {thumbnails.map((thumbnail, i) => {
                        const { alt, image, ...forwardedProps } = thumbnail;
                        return (
                            <SlideshowItem key={generateRandomWithSeed(i)}>
                                <ImageBlock
                                    alt={alt}
                                    className={element('image-block')}
                                    image={image}
                                    theme="dark"
                                    align="center"
                                    fillHeight
                                    thumbnailProps={forwardedProps}
                                />
                            </SlideshowItem>
                        );
                    })}
                </Slideshow>
            </ClickAwayProvider>
        </Lightbox>
    );
};
